import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface ChangeLogEntry {
  changed_at: string;
  schema_name: string;
  table_name: string;
  operation: string;
  record_key: string;
  record_value: any;
  column_changes: any;
  changed_by: string;
  session_username: string;
  application_name: string;
  client_addr: string;
}

const AuditTrail: React.FC = () => {
  const [auditEntries, setAuditEntries] = useState<ChangeLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [schemaFilter, setSchemaFilter] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [operationFilter, setOperationFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<ChangeLogEntry | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const operationOptions = [
    { value: '', label: 'All Operations' },
    { value: 'INSERT', label: 'Insert' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
  ];

  const schemaOptions = [
    { value: '', label: 'All Schemas' },
    { value: 'ebr', label: 'EBR' },
    { value: 'shared', label: 'Shared' },
    { value: 'app_audit', label: 'App Audit' },
  ];

  useEffect(() => {
    fetchAuditTrail();
  }, []);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAuditTrail();
      setAuditEntries(data.results || data);
    } catch (error) {
      console.error('Error fetching audit trail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (schemaFilter) params.schema_name = schemaFilter;
      if (tableFilter) params.table_name = tableFilter;
      if (operationFilter) params.operation = operationFilter;
      if (userFilter) params.changed_by = userFilter;
      if (searchQuery.trim()) params.search = searchQuery;

      const data = await apiService.getAuditTrail(params);
      setAuditEntries(data.results || data);
    } catch (error) {
      console.error('Error searching audit trail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setSchemaFilter('');
    setTableFilter('');
    setOperationFilter('');
    setUserFilter('');
    fetchAuditTrail();
  };

  const handleViewDetails = (entry: ChangeLogEntry) => {
    setSelectedEntry(entry);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedEntry(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'INSERT':
        return 'success';
      case 'UPDATE':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderJsonData = (data: any, title: string) => {
    if (!data) return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.875rem',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom>
        Audit Trail
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        View all changes made to the system with detailed tracking information
      </Typography>

      {/* Search Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search record key"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter record identifier..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Schema</InputLabel>
                <Select
                  value={schemaFilter}
                  label="Schema"
                  onChange={(e) => setSchemaFilter(e.target.value)}
                >
                  {schemaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Table</InputLabel>
                <Select
                  value={tableFilter}
                  label="Table"
                  onChange={(e) => setTableFilter(e.target.value)}
                >
                  <MenuItem value="">All Tables</MenuItem>
                  {Array.from(new Set(auditEntries.map(e => e.table_name).filter(Boolean))).sort().map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Operation</InputLabel>
                <Select
                  value={operationFilter}
                  label="Operation"
                  onChange={(e) => setOperationFilter(e.target.value)}
                >
                  {operationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={userFilter}
                  label="User"
                  onChange={(e) => setUserFilter(e.target.value)}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {Array.from(new Set(auditEntries.map(e => e.changed_by).filter(Boolean))).sort().map(u => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={1}>
              <Box display="flex" gap={1} flexDirection="column">
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  size="small"
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Trail Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Audit Log
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {auditEntries.length} entries found
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Schema.Table</TableCell>
                    <TableCell>Operation</TableCell>
                    <TableCell>Record Key</TableCell>
                    <TableCell>Changed By</TableCell>
                    <TableCell>Session User</TableCell>
                    <TableCell>Application</TableCell>
                    <TableCell>Client IP</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="textSecondary">
                          No audit entries found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditEntries.map((entry, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(entry.changed_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {entry.schema_name}.{entry.table_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.operation}
                            size="small"
                            color={getOperationColor(entry.operation) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {entry.record_key}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.changed_by}</TableCell>
                        <TableCell>{entry.session_username}</TableCell>
                        <TableCell>{entry.application_name}</TableCell>
                        <TableCell>{entry.client_addr}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewDetails(entry)}
                            sx={{ textTransform: 'none' }}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={closeDetailsDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          Audit Entry Details
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedEntry.changed_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Table
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.schema_name}.{selectedEntry.table_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Operation
                  </Typography>
                  <Chip
                    label={selectedEntry.operation}
                    size="small"
                    color={getOperationColor(selectedEntry.operation) as any}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Record Key
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedEntry.record_key}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Changed By
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.changed_by}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Session User
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.session_username}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Application
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.application_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Client IP
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.client_addr}
                  </Typography>
                </Grid>
              </Grid>

              {renderJsonData(selectedEntry.record_value, 'Record Value')}
              {selectedEntry.column_changes && 
                renderJsonData(selectedEntry.column_changes, 'Column Changes')}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrail;